import { crmSupabase } from "./crm-client";

// ----------------------------------------------------------------------------
// All mutations on crm.*. If crmSupabase is null (env vars missing), the
// callers should keep showing mock data; we just return early.
// ----------------------------------------------------------------------------

export async function toggleEstrella(leadId: string, next: boolean) {
  if (!crmSupabase) return;
  const { error } = await crmSupabase
    .from("lead")
    .update({
      is_estrella: next,
      estrella_since: next ? new Date().toISOString() : null,
    })
    .eq("id", leadId);
  if (error) throw error;
}

export async function exitLead(leadId: string, reason: string) {
  if (!crmSupabase) return;
  const { error } = await crmSupabase
    .from("lead")
    .update({
      status: "exited",
      exited_at: new Date().toISOString(),
      exited_reason: reason || "manual_exit",
    })
    .eq("id", leadId);
  if (error) throw error;
}

export async function markActionDone(actionId: string) {
  if (!crmSupabase) return;
  const { error } = await crmSupabase
    .from("next_action")
    .update({ status: "done", done_at: new Date().toISOString() })
    .eq("id", actionId);
  if (error) throw error;
}

export async function restoreAction(actionId: string) {
  if (!crmSupabase) return;
  const { error } = await crmSupabase
    .from("next_action")
    .update({ status: "open", done_at: null })
    .eq("id", actionId);
  if (error) throw error;
}

export async function restoreLeadFromExit(leadId: string) {
  if (!crmSupabase) return;
  const { error } = await crmSupabase
    .from("lead")
    .update({ status: "active", exited_at: null, exited_reason: null })
    .eq("id", leadId);
  if (error) throw error;
}

export async function updateActionWhat(actionId: string, what: string) {
  if (!crmSupabase) return;
  const { error } = await crmSupabase
    .from("next_action")
    .update({ what })
    .eq("id", actionId);
  if (error) throw error;
}

export async function logIndividualMessage(args: {
  lead_id: string;
  channel: "email" | "whatsapp";
  source_account: string;
  subject?: string;
  body_text?: string;
}) {
  if (!crmSupabase) return;
  const { error } = await crmSupabase.from("individual_message").insert({
    lead_id: args.lead_id,
    channel: args.channel,
    source_account: args.source_account,
    subject: args.subject ?? null,
    body_text: args.body_text ?? "(manual action logged from CRM UI)",
    sent_at: new Date().toISOString(),
  });
  if (error) throw error;
  // Also bump last_contact_at on the lead
  await crmSupabase
    .from("lead")
    .update({ last_contact_at: new Date().toISOString() })
    .eq("id", args.lead_id);
}

export async function queueLeadForTouchpoint(args: {
  lead_id: string;
  touchpoint_id: string;
  batch_id: string;
  channel: "email" | "whatsapp";
}) {
  if (!crmSupabase) return;
  const { error } = await crmSupabase.from("touchpoint_send").insert({
    lead_id: args.lead_id,
    touchpoint_id: args.touchpoint_id,
    batch_id: args.batch_id,
    channel: args.channel,
    status: "pending",
  });
  if (error) throw error;
}

// ----------------------------------------------------------------------------
// Conversation thread (Jeremy ↔ Ethan), per lead
// ----------------------------------------------------------------------------

export type ConversationMessage = {
  id: string;
  lead_id: string;
  from_role: "jeremy" | "ethan";
  body: string;
  sent_by_email: boolean;
  emailed_at: string | null;
  created_at: string;
};

export async function listMessages(leadId: string): Promise<ConversationMessage[]> {
  if (!crmSupabase) return [];
  const { data, error } = await crmSupabase
    .from("conversation_message")
    .select("*")
    .eq("lead_id", leadId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data as ConversationMessage[]) ?? [];
}

export async function postMessage(args: {
  lead_id: string;
  body: string;
  sent_by_email: boolean;
}): Promise<ConversationMessage | null> {
  if (!crmSupabase) return null;
  const { data, error } = await crmSupabase
    .from("conversation_message")
    .insert({
      lead_id: args.lead_id,
      from_role: "jeremy",
      body: args.body,
      sent_by_email: args.sent_by_email,
      emailed_at: args.sent_by_email ? new Date().toISOString() : null,
    })
    .select()
    .single();
  if (error) throw error;
  return data as ConversationMessage;
}

// ----------------------------------------------------------------------------
// Settings (assistant_email is editable from Réglages)
// ----------------------------------------------------------------------------

export async function getSetting(key: string): Promise<string | null> {
  if (!crmSupabase) return null;
  const { data, error } = await crmSupabase
    .from("app_setting")
    .select("value")
    .eq("key", key)
    .maybeSingle();
  if (error) throw error;
  return (data?.value as string) ?? null;
}

// ----------------------------------------------------------------------------
// Video upload (AWS S3 via Edge Function presigned URL)
//
// Why S3 and not Supabase Storage : Supabase free tier has a hard 50 MB cap
// per file, and we want to host real videos (up to several hundred MB). S3 has
// no per-file limit and we already pay for the videolink-prod account.
// The bucket `llyll-crm-public-776051122746` is public-read for direct serving
// in emails ; uploads are gated behind a Supabase Edge Function that mints a
// presigned PUT URL using a scoped IAM user.
// ----------------------------------------------------------------------------

/** Extract a single frame from a video file as a JPEG Blob, via Canvas. */
function extractVideoThumbnail(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    const url = URL.createObjectURL(file);
    video.src = url;
    let cleanedUp = false;
    const cleanup = () => {
      if (!cleanedUp) {
        cleanedUp = true;
        URL.revokeObjectURL(url);
      }
    };
    video.addEventListener("loadedmetadata", () => {
      video.currentTime = Math.min(1, video.duration / 4);
    });
    video.addEventListener("seeked", () => {
      try {
        const canvas = document.createElement("canvas");
        const w = video.videoWidth || 640;
        const h = video.videoHeight || 360;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("no 2d context");
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            cleanup();
            if (blob) resolve(blob);
            else reject(new Error("canvas.toBlob returned null"));
          },
          "image/jpeg",
          0.85,
        );
      } catch (e) {
        cleanup();
        reject(e);
      }
    });
    video.addEventListener("error", () => {
      cleanup();
      reject(new Error("video failed to load"));
    });
  });
}

export type VideoUploadResult = {
  videoUrl: string;
  thumbUrl: string;
};

/** Ask the Edge Function for a presigned S3 PUT URL, then PUT the blob. */
async function uploadToS3(
  blob: Blob,
  filename: string,
  contentType: string,
  tpId: string,
  kind: "video" | "thumb",
): Promise<string> {
  if (!crmSupabase) throw new Error("no supabase client");
  const { data, error } = await crmSupabase.functions.invoke(
    "s3-presigned-upload",
    { body: { filename, contentType, tpId, kind } },
  );
  if (error) throw error;
  if (!data || (data as any).error) {
    throw new Error((data as any)?.error ?? "presign failed");
  }
  const { uploadUrl, publicUrl } = data as {
    uploadUrl: string;
    publicUrl: string;
  };
  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    body: blob,
    headers: { "Content-Type": contentType },
  });
  if (!putRes.ok) {
    const text = await putRes.text().catch(() => "");
    throw new Error(`S3 PUT failed: ${putRes.status} ${text.slice(0, 200)}`);
  }
  return publicUrl;
}

/** Capture a frame from a publicly-readable video URL at the given timestamp
 *  (seconds), then upload as the touchpoint's thumbnail. */
export async function captureAndUploadThumbnailAt(
  videoUrl: string,
  timeSec: number,
  tpId: string,
): Promise<string> {
  if (!crmSupabase) throw new Error("no supabase client");
  const blob = await new Promise<Blob>((resolve, reject) => {
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    video.src = videoUrl;
    video.addEventListener("loadedmetadata", () => {
      const target = Math.max(
        0,
        Math.min(timeSec, Math.max(0, (video.duration || 0) - 0.05)),
      );
      video.currentTime = target;
    });
    video.addEventListener("seeked", () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 360;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("no 2d context");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("toBlob null"))),
          "image/jpeg",
          0.9,
        );
      } catch (e) {
        reject(e);
      }
    });
    video.addEventListener("error", () =>
      reject(new Error("video load failed (CORS?)")),
    );
  });
  const thumbUrl = await uploadToS3(blob, "thumbnail.jpg", "image/jpeg", tpId, "thumb");
  await crmSupabase.from("touchpoint").update({ video_thumb_url: thumbUrl }).eq("id", tpId);
  return thumbUrl;
}

/** Upload an avatar image (the round picture in the video card corner). */
export async function uploadAvatar(file: File, tpId: string): Promise<string> {
  if (!crmSupabase) throw new Error("no supabase client");
  if (!file.type.startsWith("image/")) {
    throw new Error("Le fichier doit être une image (PNG/JPG/WebP).");
  }
  const url = await uploadToS3(file, file.name, file.type, tpId, "thumb");
  await crmSupabase.from("touchpoint").update({ avatar_url: url }).eq("id", tpId);
  return url;
}

export async function uploadVideoAndThumb(
  file: File,
  tpId: string,
  onProgress?: (label: string) => void,
): Promise<VideoUploadResult> {
  if (!crmSupabase) throw new Error("no supabase client");

  onProgress?.("Extraction de la thumbnail…");
  let thumbBlob: Blob | null = null;
  try {
    thumbBlob = await extractVideoThumbnail(file);
  } catch (e) {
    console.warn("thumbnail extraction failed", e);
  }

  onProgress?.(`Upload vidéo (${(file.size / 1024 / 1024).toFixed(1)} MB)…`);
  const videoUrl = await uploadToS3(
    file,
    file.name,
    file.type || "video/mp4",
    tpId,
    "video",
  );

  let thumbUrl = "";
  if (thumbBlob) {
    onProgress?.("Upload thumbnail…");
    try {
      thumbUrl = await uploadToS3(
        thumbBlob,
        "thumbnail.jpg",
        "image/jpeg",
        tpId,
        "thumb",
      );
    } catch (e) {
      console.warn("thumb upload failed", e);
    }
  }

  onProgress?.("Sauvegarde des URLs…");
  await crmSupabase
    .from("touchpoint")
    .update({ video_url: videoUrl, video_thumb_url: thumbUrl || null })
    .eq("id", tpId);

  onProgress?.("Terminé ✓");
  return { videoUrl, thumbUrl };
}

// ----------------------------------------------------------------------------
// Touchpoints (templates)
// ----------------------------------------------------------------------------

export type TouchpointPatch = {
  title?: string;
  body_text?: string | null;
  body_html?: string | null;
  body_template?: Record<string, unknown> | null;
  video_url?: string | null;
  cta_label?: string | null;
  channel?: "email" | "whatsapp";
  status?: "draft" | "ready" | "archived";
};

export async function updateTouchpoint(id: string, patch: TouchpointPatch) {
  if (!crmSupabase) return;
  const { error } = await crmSupabase.from("touchpoint").update(patch).eq("id", id);
  if (error) throw error;
}

export async function duplicateTouchpoint(srcId: string) {
  if (!crmSupabase) return null;
  const { data: src, error: e1 } = await crmSupabase
    .from("touchpoint")
    .select("*")
    .eq("id", srcId)
    .single();
  if (e1 || !src) throw e1;
  const { id: _id, created_at: _ca, published_at: _pa, ...rest } = src as any;
  void _id;
  void _ca;
  void _pa;
  // find next available number suffix
  const newNumber = `${src.number}-copie-${Date.now().toString().slice(-4)}`;
  const { data: ins, error: e2 } = await crmSupabase
    .from("touchpoint")
    .insert({
      ...rest,
      number: newNumber,
      parent_tp_id: srcId,
      title: `Copie de · ${src.title}`,
      status: "draft",
      published_at: null,
    })
    .select()
    .single();
  if (e2) throw e2;
  return ins;
}

export async function createTouchpoint(args: {
  number: string;
  style: "plain" | "wanted" | "drama" | "sticker" | "info";
  title: string;
  channel?: "email" | "whatsapp";
  body_text?: string;
}) {
  if (!crmSupabase) return null;
  const { data, error } = await crmSupabase
    .from("touchpoint")
    .insert({
      number: args.number,
      style: args.style,
      title: args.title,
      channel: args.channel ?? "email",
      body_text: args.body_text ?? "",
      status: "draft",
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ----------------------------------------------------------------------------
// Batches
// ----------------------------------------------------------------------------

export type CurrentBatch = {
  id: string;
  status: string;
  default_touchpoint_id: string | null;
  created_at: string;
  notes_md: string | null;
  scheduled_for?: string | null;
};

export async function scheduleBatch(batchId: string, scheduledForIso: string) {
  if (!crmSupabase) return;
  const { error } = await crmSupabase
    .from("touchpoint_batch")
    .update({
      status: "scheduled",
      scheduled_for: scheduledForIso,
      locked_at: new Date().toISOString(),
    })
    .eq("id", batchId);
  if (error) throw error;
}

export async function getOrCreateDraftBatch(
  defaultTpId: string | null,
): Promise<CurrentBatch> {
  if (!crmSupabase) throw new Error("no client");
  const { data: existing } = await crmSupabase
    .from("touchpoint_batch")
    .select("*")
    .in("status", ["draft", "matrix_locked"])
    .order("created_at", { ascending: false })
    .limit(1);
  if (existing && existing.length > 0) return existing[0] as CurrentBatch;
  const { data: created, error } = await crmSupabase
    .from("touchpoint_batch")
    .insert({
      mode: "sync",
      status: "draft",
      default_touchpoint_id: defaultTpId,
    })
    .select()
    .single();
  if (error) throw error;
  return created as CurrentBatch;
}

export async function clearBatchSends(batchId: string) {
  if (!crmSupabase) return;
  // Remove any non-sent sends from this batch so we can re-queue from scratch
  await crmSupabase
    .from("touchpoint_send")
    .delete()
    .eq("batch_id", batchId)
    .in("status", ["pending", "pending_manual", "skipped"]);
}

export async function queueSends(
  batchId: string,
  items: { lead_id: string; touchpoint_id: string; channel: "email" | "whatsapp" }[],
) {
  if (!crmSupabase || items.length === 0) return;
  const rows = items.map((it) => ({
    batch_id: batchId,
    lead_id: it.lead_id,
    touchpoint_id: it.touchpoint_id,
    channel: it.channel,
    status: "pending" as const,
  }));
  const { error } = await crmSupabase.from("touchpoint_send").insert(rows);
  if (error) throw error;
}

export async function lockAndSendBatch(batchId: string) {
  if (!crmSupabase) return;
  const { error } = await crmSupabase
    .from("touchpoint_batch")
    .update({
      status: "sending",
      locked_at: new Date().toISOString(),
    })
    .eq("id", batchId);
  if (error) throw error;
}

export async function markBatchSent(batchId: string) {
  if (!crmSupabase) return;
  await crmSupabase
    .from("touchpoint_batch")
    .update({ status: "sent", sent_at: new Date().toISOString() })
    .eq("id", batchId);
}

export async function markSendAsSent(sendId: string, externalId?: string) {
  if (!crmSupabase) return;
  await crmSupabase
    .from("touchpoint_send")
    .update({
      status: "sent",
      sent_at: new Date().toISOString(),
      external_id: externalId ?? null,
    })
    .eq("id", sendId);
}

export async function markSendAsFailed(sendId: string, errorMessage: string) {
  if (!crmSupabase) return;
  await crmSupabase
    .from("touchpoint_send")
    .update({
      status: "failed",
      error_message: errorMessage,
    })
    .eq("id", sendId);
}

// ----------------------------------------------------------------------------
// Edge Function bridge — calls Supabase Edge Function "send-email" which
// forwards to Resend with a safelist (jeremy+N@llyll.com in test).
// ----------------------------------------------------------------------------

export type SendEmailPayload = {
  to: string;
  subject: string;
  body_text: string;
  body_html?: string;
  from?: string;
};

export type SendEmailResult = {
  ok: boolean;
  external_id?: string;
  skipped?: boolean;
  skip_reason?: string;
  error?: string;
};

export async function sendEmailViaEdge(
  payload: SendEmailPayload,
): Promise<SendEmailResult> {
  if (!crmSupabase) return { ok: false, error: "no supabase client" };
  const { data, error } = await crmSupabase.functions.invoke("send-email", {
    body: payload,
  });
  if (error) return { ok: false, error: error.message };
  return data as SendEmailResult;
}

export async function setSetting(key: string, value: string) {
  if (!crmSupabase) return;
  // Upsert via update + insert if not present
  const { data: existing } = await crmSupabase
    .from("app_setting")
    .select("key")
    .eq("key", key)
    .maybeSingle();
  if (existing) {
    const { error } = await crmSupabase
      .from("app_setting")
      .update({ value, updated_at: new Date().toISOString() })
      .eq("key", key);
    if (error) throw error;
  } else {
    const { error } = await crmSupabase
      .from("app_setting")
      .insert({ key, value });
    if (error) throw error;
  }
}
